/**
 * Chute test arithmetic and acceptance rules — the hot path during a pour.
 *
 * Spec, "Computed fields and acceptance rules":
 *   W/C ratio | water_vol_l / grout_wt_kg | -- (displayed live)
 *   Spread    | entered | 16-19 in -> in range / add water (<16) / reduce water (>19)
 *   Flow      | entered | 40-70 s  -> in range / too much water (<40) / not enough (>70)
 *   Mud balance: captured in lb/ft3 with NO verdict, because no acceptance
 *   range has been supplied and the app must not invent one for a QA record.
 */

import {
  FLOW_MAX_S,
  FLOW_MIN_S,
  SPREAD_MAX_IN,
  SPREAD_MIN_IN,
  type ChuteTestComputed,
  type FlowVerdict,
  type SpreadVerdict
} from '../types';
import { divide, toFinite, truncateTo, type Numeric } from './numbers';

/** Decimal places the existing workbook prints the W/C ratio to. */
export const WC_RATIO_DISPLAY_DECIMALS = 2;

/**
 * Water/cement ratio: `water_vol_l / grout_wt_kg`, at full precision.
 *
 * Returns `null` when either input is missing or `groutWtKg` is zero — a
 * half-entered row shows a blank W/C, never `NaN` or `Infinity`.
 *
 * Real workbook value: 989.1 L over 5460 kg -> 0.1812, printed as 0.18.
 */
export function wcRatio(waterVolL: Numeric, groutWtKg: Numeric): number | null {
  return divide(waterVolL, groutWtKg);
}

/**
 * W/C as the workbook prints it: truncated to 2 dp, not rounded.
 *
 * The sheet's own figures prove truncation — 1990.2 / 11247 = 0.176954 is
 * recorded as 0.17, and 2906.4 / 16361 = 0.177642 as 0.17. Rounding would print
 * 0.18 for both and our arithmetic would no longer reproduce the sheet's.
 * Use for display and export only; never feed this back into a calculation.
 */
export function wcRatioDisplay(waterVolL: Numeric, groutWtKg: Numeric): number | null {
  return truncateTo(wcRatio(waterVolL, groutWtKg), WC_RATIO_DISPLAY_DECIMALS);
}

/**
 * Spread verdict, INCHES (the workbook's "mm" column label is an error —
 * confirmed by Geoff 2026-07-28).
 *
 * Target 16-19 in, inclusive at both ends. Below 16 add water; above 19 reduce
 * the water dosage rate (Appendix C step 9).
 *
 * Returns `null` when nothing has been entered. An out-of-range reading is
 * flagged, never blocked — recording it is the entire point.
 */
export function spreadVerdict(inches: Numeric): SpreadVerdict | null {
  const value = toFinite(inches);
  if (value === null) return null;
  if (value < SPREAD_MIN_IN) return 'add_water';
  if (value > SPREAD_MAX_IN) return 'reduce_water';
  return 'in_range';
}

/**
 * Flow verdict, SECONDS. Target 40-70 s, inclusive at both ends. Below 40 there
 * is too much water; above 70 there is not enough (Appendix C step 10).
 *
 * Returns `null` when nothing has been entered.
 */
export function flowVerdict(seconds: Numeric): FlowVerdict | null {
  const value = toFinite(seconds);
  if (value === null) return null;
  if (value < FLOW_MIN_S) return 'too_much_water';
  if (value > FLOW_MAX_S) return 'not_enough_water';
  return 'in_range';
}

/**
 * Mud balance is CAPTURE ONLY.
 *
 * `verdict` is typed as `null` so no caller can ever assert a pass or a fail
 * against a range nobody has supplied. Give it limits and this becomes a real
 * verdict function; until then the type system enforces the spec's open item.
 */
export interface MudBalanceCapture {
  /** As entered, or `null`. */
  value: number | null;
  /** lb/ft3, per the spec's units decision. */
  unit: 'lb/ft3';
  /** Always `null` — no acceptance range has been supplied. */
  verdict: null;
}

/** Record a mud balance reading. Never judges it. */
export function mudBalance(value: Numeric): MudBalanceCapture {
  return { value: toFinite(value), unit: 'lb/ft3', verdict: null };
}

/** A chute test row, as loosely typed as the form that fills it. */
export interface ChuteTestInput {
  id: number;
  spread_in?: Numeric;
  flow_seconds?: Numeric;
  water_vol_l?: Numeric;
  grout_wt_kg?: Numeric;
}

/** Everything computed for one chute test row. */
export function chuteTestComputed(test: ChuteTestInput): ChuteTestComputed {
  return {
    chute_test_id: test.id,
    wc_ratio: wcRatio(test.water_vol_l, test.grout_wt_kg),
    spread_verdict: spreadVerdict(test.spread_in),
    flow_verdict: flowVerdict(test.flow_seconds),
    mud_balance_verdict: null
  };
}
